import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, FileBadge, MapPin, Mail, Scale, Globe } from 'lucide-react';
import LightBackground from '@/components/LightBackground';

const LegalNotice = () => {
  const lastUpdated = 'May 13, 2026';

  const rows = [
    { icon: Building2, label: 'Legal Entity', value: 'AURADYNAMICS PROJECT MANAGEMENT SERVICES - FZCO' },
    { icon: Scale, label: 'Legal Form', value: 'Free Zone Company (FZCO)' },
    { icon: Globe, label: 'Holding Company Website', value: 'auradynamics.management' },
    { icon: FileBadge, label: 'Trade License No.', value: '87988' },
    { icon: FileBadge, label: 'Issuing Authority', value: 'Dubai Integrated Economic Zones Authority (DIEZ) — IFZA' },
    { icon: FileBadge, label: 'License Issue Date', value: '06 May 2026' },
    { icon: FileBadge, label: 'License Expiry Date', value: '05 May 2027' },
    { icon: MapPin, label: 'Registered Address', value: 'DSO-IFZA, IFZA Properties, Dubai Silicon Oasis, Dubai, United Arab Emirates' },
    { icon: Mail, label: 'Contact Email', value: 'info@facelessaura.club' },
    { icon: Mail, label: 'Support Email', value: 'hello@auradynamics.management' },
  ];

  return (
    <>
      <Helmet>
        <title>Legal Notice / Imprint – Faceless Aura Club</title>
        <meta
          name="description"
          content="Legal notice and imprint for Faceless Aura Club, a brand operated by AURADYNAMICS PROJECT MANAGEMENT SERVICES - FZCO, licensed in Dubai, UAE."
        />
        <link rel="canonical" href="https://facelessaura.club/legal" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative">
        <LightBackground />

        <header className="relative z-10 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </header>

        <main className="relative z-10 max-w-4xl mx-auto px-6 py-12 md:py-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 mb-6">
              <Scale className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3">Legal Notice</h1>
            <p className="text-slate-500">Last updated: {lastUpdated}</p>
          </div>

          <div className="bg-white/70 backdrop-blur-sm border border-slate-200 rounded-2xl p-6 md:p-10 space-y-8 text-slate-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Information About the Operator</h2>
              <p>
                This website (<span className="font-semibold">facelessaura.club</span>) and the Faceless Aura Club™
                brand are owned and operated by the legal entity below.
              </p>
            </section>

            <section>
              <div className="grid sm:grid-cols-2 gap-4">
                {rows.map((r) => (
                  <div key={r.label} className="flex items-start gap-3 border border-slate-200 rounded-xl p-4 bg-white/50">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <r.icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{r.label}</p>
                      <p className="text-slate-900 font-medium break-words">{r.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Licensed Activities</h2>
              <p>
                Under license No. 87988 issued by the Dubai Integrated Economic Zones Authority (DIEZ / IFZA), the
                company is authorized to conduct the following activities: <strong>Portal</strong>,{' '}
                <strong>Marketing Management</strong>, <strong>Project Management Services</strong>, and{' '}
                <strong>E-commerce</strong>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Responsibility for Content</h2>
              <p>
                The content on this website is provided for educational and informational purposes only. While we
                make every effort to ensure accuracy, we accept no liability for the completeness, timeliness, or
                accuracy of the information presented. Use of any information is at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Earnings Disclaimer</h2>
              <p>
                Any income, revenue, or business results referenced on this website are illustrative examples and do
                not constitute a guarantee of future performance. Outcomes depend on individual effort, skill,
                market conditions, and execution. Faceless Aura Club is not a get-rich-quick program.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">External Links</h2>
              <p>
                Our website may contain links to third-party websites. We have no influence over their content and
                assume no responsibility for them. The respective operators are solely responsible for the content
                of linked pages.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Intellectual Property</h2>
              <p>
                All content on this website — including text, graphics, logos, the Faceless Aura Club™ brand, the
                Aura AI mascot, and software — is the property of AURADYNAMICS PROJECT MANAGEMENT SERVICES - FZCO
                or its licensors and is protected by international copyright and trademark laws. Reproduction
                without prior written consent is prohibited.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Governing Law & Jurisdiction</h2>
              <p>
                This Legal Notice and any disputes arising from the use of this website are governed by the laws of
                the United Arab Emirates and the regulations of the Dubai Integrated Economic Zones Authority. Place
                of jurisdiction is Dubai, UAE.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Related Documents</h2>
              <ul className="list-disc list-inside space-y-1 text-blue-600">
                <li><Link to="/terms" className="hover:underline">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:underline">Privacy Policy</Link></li>
                <li><Link to="/cookies" className="hover:underline">Cookie Policy</Link></li>
                <li><Link to="/refunds" className="hover:underline">Refund Policy</Link></li>
              </ul>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default LegalNotice;
